import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import RealtimeBadge from '../../components/CallcenterV2/RealtimeBadge';
import { getCcv2ApiBase, getCcv2Token, getCcv2TenantId } from '../../services/callcenterV2';
import { extractExtension, displayAgentName, isUsefulAgent, displayQueue, extensionsInActiveAssignments } from './utils';
import '../../components/CallcenterV2/ccv2-components.css';
import './styles.css';

const PREFS_KEY = 'ccv2_wallboard_prefs';
const DEFAULT_PREFS = { tvMode: false };

const formatDuration = (s) => {
  if (!s || s <= 0) return '0s';
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

// effectiveInCall takes priority over device state because Local/<ext>@from-queue
// QueueMembers (used to ring SIP + WebRTC simultaneously) never carry device
// state — only the connected assignment is authoritative for "agent is on a call".
const getAgentStatusClass = (agent) => {
  if (agent.paused) return 'paused';
  if (agent.effectiveInCall) return 'busy';
  if (agent.status === 1 || agent.status === 0) return 'available';
  return 'offline';
};

function Realtime() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [lastUpdateAt, setLastUpdateAt] = useState(null);
  const [prefs, setPrefs] = useState(() => {
    try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; }
    catch (_) { return DEFAULT_PREFS; }
  });

  const [state, setState] = useState({ agents: {}, queues: {}, calls: {}, assignments: {}, lastSeq: 0, tenantPrefix: null });
  const [metrics, setMetrics] = useState({});
  const socketRef = useRef(null);
  const mountedRef = useRef(true);
  const metricsTimerRef = useRef(null);

  const savePrefs = useCallback((update) => {
    const next = { ...prefs, ...update };
    setPrefs(next);
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch (_) {}
  }, [prefs]);

  const fetchMetrics = useCallback(async () => {
    try {
      const base = await getCcv2ApiBase();
      const tenantId = getCcv2TenantId();
      const token = getCcv2Token();
      const res = await fetch(`${base}/v2/analytics/metrics/${tenantId}/kpis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok && mountedRef.current) {
        const data = await res.json();
        setMetrics(data);
        setLastUpdateAt(new Date());
      }
    } catch (_) {}
  }, []);

  const applyEvent = useCallback((event) => {
    setState((prev) => {
      const MAP = { agent: 'agents', queue: 'queues', call: 'calls', assignment: 'assignments' };
      const key = MAP[event.entityType];
      if (!key) return prev;
      const isDel = event.type.endsWith('.delete') || event.type.endsWith('.ended') || event.type.endsWith('.left') || event.type.endsWith('.abandoned');
      const isUps = event.type.endsWith('.upsert') || event.type.endsWith('.queued') || event.type.endsWith('.ringing') || event.type.endsWith('.answered');
      // Reject orphan agent upserts (interface=null/undefined from initial AMI enumeration).
      if (isUps && event.entityType === 'agent' && !isUsefulAgent(event.payload)) return prev;
      const ns = { ...prev };
      if (isUps) ns[key] = { ...prev[key], [event.entityId]: event.payload };
      else if (isDel) { const u = { ...prev[key] }; delete u[event.entityId]; ns[key] = u; }
      ns.lastSeq = Math.max(prev.lastSeq, event.seq);
      return ns;
    });
    setLastUpdateAt(new Date());
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let socket = null;

    const connect = async () => {
      try {
        const wsBase = await getCcv2ApiBase();
        const tenantId = getCcv2TenantId();
        const token = getCcv2Token();

        socket = io(`${wsBase}/v2/callcenter`, {
          path: '/ccv2-socket/',
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionDelayMax: 30000,
          reconnectionAttempts: Infinity,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          if (!mountedRef.current) return;
          setConnected(true);
          setReconnectAttempt(0);
          setError(null);
          socket.emit('subscribe', { token, tenantId, afterSeq: 0 }, (resp) => {
            if (!mountedRef.current) return;
            if (resp?.snapshot) {
              const toMap = (arr) => {
                const m = {};
                if (Array.isArray(arr)) arr.forEach((it) => { const id = it.memberId || it.callId || it.queueId || it.assignmentId || it.id; if (id) m[id] = it; });
                else if (typeof arr === 'object' && arr) return arr;
                return m;
              };
              setState({ agents: toMap(resp.snapshot.agents), queues: toMap(resp.snapshot.queues), calls: toMap(resp.snapshot.calls), assignments: toMap(resp.snapshot.assignments), lastSeq: resp.snapshot.seq || 0, tenantPrefix: resp.snapshot.tenant?.namePrefix ?? null });
            }
            setLoading(false);
          });
          fetchMetrics();
          metricsTimerRef.current = setInterval(fetchMetrics, 30000);
        });

        socket.on('disconnect', () => { if (mountedRef.current) setConnected(false); });
        socket.on('reconnect_attempt', (n) => { if (mountedRef.current) setReconnectAttempt(n); });
        socket.on('reconnect', () => { if (mountedRef.current) { setReconnectAttempt(0); fetchMetrics(); } });
        socket.on('event', (ev) => { if (mountedRef.current) applyEvent(ev); });
        socket.on('delta', (d) => { if (mountedRef.current && d?.events) d.events.forEach(applyEvent); });
        socket.on('connect_error', (e) => { if (mountedRef.current) { setError(`Erro: ${e.message}`); setLoading(false); } });
      } catch (e) {
        if (mountedRef.current) { setError(e.message); setLoading(false); }
      }
    };

    connect();
    return () => {
      mountedRef.current = false;
      clearInterval(metricsTimerRef.current);
      if (socketRef.current) { socketRef.current.emit('unsubscribe'); socketRef.current.disconnect(); }
    };
  }, [applyEvent, fetchMetrics]);

  const queueList = useMemo(() => Object.values(state.queues), [state.queues]);
  const agentList = useMemo(() => {
    const m = new Map();
    Object.values(state.agents).forEach((a) => {
      if (!isUsefulAgent(a)) return;
      const iface = a.interface || '';
      const key = a.extension || extractExtension(iface) || iface || a.memberId;
      if (!key) return;
      const ex = m.get(key);
      if (!ex) {
        m.set(key, { ...a, extension: a.extension || extractExtension(iface), queues: a.queue ? [a.queue] : [] });
      } else {
        const isPjsipNew = iface.startsWith('PJSIP/');
        const isPjsipOld = (ex.interface || '').startsWith('PJSIP/');
        if (isPjsipNew && !isPjsipOld) ex.interface = iface;
        if (a.memberName) {
          const newClean = !/^(?:PJSIP|Local|SIP|IAX2)\//.test(String(a.memberName));
          const oldClean = ex.memberName && !/^(?:PJSIP|Local|SIP|IAX2)\//.test(String(ex.memberName));
          if (newClean || !oldClean) ex.memberName = a.memberName;
        }
        if (a.queue && !ex.queues.includes(a.queue)) ex.queues.push(a.queue);
        ex.inCall = ex.inCall || a.inCall;
        if (a.paused) { ex.paused = true; ex.pausedReason = a.pausedReason || ex.pausedReason; }
        if ((a.status || 0) > (ex.status || 0)) ex.status = a.status;
      }
    });
    const inCallExts = extensionsInActiveAssignments(state.assignments);
    for (const agent of m.values()) {
      const ext = agent.extension ? String(agent.extension) : null;
      agent.effectiveInCall = !!agent.inCall || (!!ext && inCallExts.has(ext));
    }
    return Array.from(m.values());
  }, [state.agents, state.assignments]);

  const waitingCalls = useMemo(() =>
    Object.values(state.calls).filter((c) => !['left', 'abandoned', 'hangup', 'answered'].includes(c.status) && c.queue && (c.status === 'queued' || c.status === 'ringing' || c.position > 0)),
    [state.calls],
  );

  const totalWaiting = Math.max(queueList.reduce((s, q) => s + (q.calls || 0), 0), waitingCalls.length);
  const totalAvailable = agentList.filter((a) => !a.paused && !a.effectiveInCall && a.status === 1).length;
  const totalInCall = agentList.filter((a) => a.effectiveInCall).length;
  const totalPaused = agentList.filter((a) => a.paused).length;
  const sla = Number(metrics.service_level ?? metrics.sla ?? 0).toFixed(1);
  const asa = Math.round(metrics.asa ?? 0);

  const containerClass = prefs.tvMode ? 'ccv2-wallboard ccv2-wallboard-tv' : 'ccv2-wallboard';

  return (
    <div className={containerClass}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: prefs.tvMode ? '2rem' : '1.4rem', fontWeight: 800, color: '#f1f5f9' }}>
            📺 Wallboard · Tempo Real
          </h1>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
            Seq: {state.lastSeq}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <RealtimeBadge connected={connected} lastUpdateAt={lastUpdateAt} reconnectAttempt={reconnectAttempt} />
          <button
            className="ccv2-p5-btn"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#e2e8f0', fontSize: '0.8rem' }}
            onClick={() => savePrefs({ tvMode: !prefs.tvMode })}
          >
            {prefs.tvMode ? '⊡ Sair TV' : '📺 Modo TV'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', color: '#fca5a5' }}>⚠️ {error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
          <div style={{ fontSize: '2rem' }}>⌛</div>
          <p>Conectando ao servidor de eventos...</p>
        </div>
      ) : (
        <>
          {/* Big KPIs */}
          <div className="ccv2-wallboard-kpis">
            <div className={`ccv2-wallboard-kpi ${totalWaiting > 10 ? 'kpi-red' : totalWaiting > 5 ? 'kpi-yellow' : 'kpi-green'}`}>
              <div className="ccv2-wallboard-kpi-value">{totalWaiting}</div>
              <div className="ccv2-wallboard-kpi-label">Em Espera</div>
            </div>
            <div className={`ccv2-wallboard-kpi ${totalAvailable > 0 ? 'kpi-green' : 'kpi-red'}`}>
              <div className="ccv2-wallboard-kpi-value">{totalAvailable}</div>
              <div className="ccv2-wallboard-kpi-label">Disponíveis</div>
            </div>
            <div className="ccv2-wallboard-kpi kpi-blue">
              <div className="ccv2-wallboard-kpi-value">{totalInCall}</div>
              <div className="ccv2-wallboard-kpi-label">Em Chamada</div>
            </div>
            <div className="ccv2-wallboard-kpi kpi-yellow">
              <div className="ccv2-wallboard-kpi-value">{totalPaused}</div>
              <div className="ccv2-wallboard-kpi-label">Pausados</div>
            </div>
            <div className={`ccv2-wallboard-kpi ${Number(sla) >= 80 ? 'kpi-green' : Number(sla) >= 60 ? 'kpi-yellow' : 'kpi-red'}`}>
              <div className="ccv2-wallboard-kpi-value">{sla}%</div>
              <div className="ccv2-wallboard-kpi-label">SLA</div>
            </div>
            <div className={`ccv2-wallboard-kpi ${asa <= 30 ? 'kpi-green' : asa <= 60 ? 'kpi-yellow' : 'kpi-red'}`}>
              <div className="ccv2-wallboard-kpi-value">{formatDuration(asa)}</div>
              <div className="ccv2-wallboard-kpi-label">TME Médio</div>
            </div>
          </div>

          {/* Queues & Calls grid */}
          <div style={{ display: 'grid', gridTemplateColumns: prefs.tvMode ? '1fr 1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            {/* Waiting calls */}
            <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '1rem' }}>
              <h3 style={{ margin: '0 0 0.75rem', color: '#e2e8f0', fontSize: '1rem' }}>📞 Chamadas em Espera ({totalWaiting})</h3>
              {waitingCalls.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Nenhuma chamada em espera</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', color: '#e2e8f0' }}>
                  <thead>
                    <tr style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Telefone</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Fila</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Espera</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitingCalls.slice(0, 20).map((c) => (
                      <tr key={c.uniqueId || c.callId} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{c.callerNumber || '--'}</td>
                        <td style={{ padding: '4px 8px', fontSize: '0.78rem', color: '#94a3b8' }} title={c.queue}>{displayQueue(c.queue, state.tenantPrefix)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatDuration(c.holdTime || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Agents */}
            <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '1rem' }}>
              <h3 style={{ margin: '0 0 0.75rem', color: '#e2e8f0', fontSize: '1rem' }}>👤 Agentes ({agentList.length})</h3>
              {agentList.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Nenhum agente conectado</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', color: '#e2e8f0' }}>
                  <thead>
                    <tr style={{ color: '#94a3b8', fontSize: '0.73rem' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Agente</th>
                      <th style={{ textAlign: 'center', padding: '4px 8px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentList.slice(0, 25).sort((a, b) => {
                      if (a.effectiveInCall !== b.effectiveInCall) return a.effectiveInCall ? -1 : 1;
                      if (a.paused !== b.paused) return a.paused ? 1 : -1;
                      return 0;
                    }).map((a) => {
                      const cls = getAgentStatusClass(a);
                      const statusColors = { available: '#4ade80', paused: '#fbbf24', busy: '#f87171', offline: '#6b7280' };
                      const statusLabels = { available: 'Disponível', paused: 'Pausado', busy: 'Em chamada', offline: 'Offline' };
                      return (
                        <tr key={a.extension || a.memberId || a.interface} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <td style={{ padding: '4px 8px' }}>{displayAgentName(a)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                            <span style={{ background: `${statusColors[cls]}22`, color: statusColors[cls], padding: '2px 8px', borderRadius: 8, fontSize: '0.73rem', fontWeight: 600 }}>
                              {statusLabels[cls] || cls}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Realtime;
